<template>
  <LazyHydrate v-bind="hydrationStrategy" @hydrated="onHydration">
    <StandardWrapper
      v-if="context.company"
      v-bind="$attrs"
      ref="container"
      :blok="blok"
      width="block"
    >
      <div class="relative">
        <div class="sm:text-center">
          <h2
            class="title sm:text-center"
            :class="[
              textColorClass(blok.titleVariant, blok.bgVariant, 'text-darker'),
              { 'text-3xl': style === 'alternative' },
            ]"
          >
            {{ blok.title }}
          </h2>
          <p
            v-if="blok.paragraph"
            class="mt-6 sm:text-center"
            :class="[
              textColorClass(blok.textVariant, blok.bgVariant),
              { 'leading-text': style !== 'alternative' },
            ]"
          >
            {{ blok.paragraph }}
          </p>
        </div>
        <div :class="style === 'alternative' ? 'mt-8 lg:mt-16' : 'mt-12'">
          <form
            v-if="context.company"
            :id="`form${blok._uid}`"
            class="relative lg:flex"
            @submit="checkForm"
          >
            <div
              class="relative min-w-0 flex-1"
              :class="{
                'flex flex-col gap-2 md:flex-row md:items-center md:gap-4 lg:-mr-2':
                  style === 'alternative',
              }"
            >
              <label
                for="cta_email"
                :class="
                  style === 'alternative' ? 'whitespace-nowrap' : 'sr-only'
                "
                >{{ blok.placeholderText }}:</label
              >
              <input
                id="cta_email"
                ref="emailInput"
                v-model.trim="state.email"
                type="email"
                class="order-1 block h-12 w-full border px-5 py-3 text-base text-gray-900 placeholder-gray-700"
                :placeholder="
                  style === 'alternative'
                    ? 'voorbeeld@provider.nl'
                    : blok.placeholderText
                "
                autocomplete="email"
                required
                :aria-invalid="v$.email?.$error"
                :aria-describedby="
                  v$.email?.$error ? `alertDesktop${blok._uid}` : null
                "
                :class="inputClass"
                @keypress.enter="checkForm"
                @input="handleInput"
              />
            </div>
            <!-- Invalid input mobile -->
            <div
              class="relative block h-0 opacity-0 lg:hidden"
              :class="{
                'h-auto opacity-95': v$.email?.$error,
                hidden: !v$.email?.$error,
              }"
            >
              <div
                class="z-10 mt-2 flex rounded-sm bg-yellow-50 outline outline-1 outline-black/5"
              >
                <div
                  class="z-20 flex items-center justify-center px-3 py-1 sm:px-5 sm:py-2"
                >
                  <span class="text-yellow-500">
                    <ExclamationCircleIcon class="h-6 w-6" />
                  </span>
                  <button
                    :tabindex="v$.email?.$error ? 0 : -1"
                    :aria-hidden="!v$.email?.$error"
                    class="ml-1 inline-block cursor-pointer text-sm text-yellow-800"
                    @click.prevent="emailInput.focus()"
                  >
                    <span
                      :role="v$.email?.$error ? 'alert' : null"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {{
                        state.email ? invalidEmailMessage : requiredEmailMessage
                      }}
                    </span>
                  </button>
                </div>
                <div
                  class="absolute inset-0 z-10 rounded-sm bg-yellow-50"
                  :class="{
                    'animate-ping motion-reduce:animate-none': pingAlert,
                  }"
                />
              </div>
            </div>
            <!-- Button -->
            <div
              class="relative mt-2 lg:mt-0 lg:min-w-[33%]"
              :class="{ 'lg:ml-3': style === 'default' }"
            >
              <button
                type="submit"
                class="btn relative h-fit min-h-[3rem] lg:h-12"
                :class="[
                  isAlertCreated ? 'btn-success' : btnClass,
                  isLoading ? 'cursor-wait' : '',
                ]"
                @click.prevent="checkForm"
              >
                <div class="flex items-center justify-center">
                  <template v-if="isLoading">
                    <IconSpinner aria-hidden="true" />
                    <span class="sr-only">Bezig</span>
                  </template>
                  <div
                    v-else
                    class="flex items-center justify-center gap-2"
                    :class="{ 'flex-row-reverse': style === 'alternative' }"
                  >
                    <component
                      :is="isAlertCreated ? CheckCircleIcon : EnvelopeIcon"
                      class="h-5 w-5"
                    />
                    <span class="inline-block">{{
                      isAlertCreated
                        ? blok.buttonLabelSuccess
                        : blok.buttonLabel
                    }}</span>
                  </div>
                </div>
              </button>
            </div>
          </form>
          <!-- Invalid input desktop -->
          <div
            class="relative hidden h-0 opacity-0 lg:block"
            :class="{
              'h-auto opacity-95': v$.email?.$error,
              hidden: !v$.email?.$error,
            }"
          >
            <div
              class="z-10 mt-2 flex rounded-sm bg-yellow-50 outline outline-1 outline-black/5"
            >
              <div
                class="z-20 flex items-center justify-center px-3 py-1 sm:px-5 sm:py-2"
              >
                <span class="text-yellow-500">
                  <ExclamationCircleIcon class="h-6 w-6" />
                </span>
                <button
                  :tabindex="v$.email?.$error ? 0 : -1"
                  :aria-hidden="!v$.email?.$error"
                  class="ml-1 inline-block cursor-pointer text-sm text-yellow-800"
                  @click="emailInput.focus()"
                >
                  <span
                    :id="`alertDesktop${blok._uid}`"
                    :role="v$.email?.$error ? 'alert' : null"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {{
                      state.email ? invalidEmailMessage : requiredEmailMessage
                    }}
                  </span>
                </button>
              </div>
              <div
                class="absolute inset-0 z-10 rounded-sm bg-yellow-50"
                :class="{
                  'animate-ping motion-reduce:animate-none': pingAlert,
                }"
              />
            </div>
          </div>
          <!-- Success -->
          <div
            class="opacity-0"
            :class="{
              'opacity-95': isAlertCreated,
              hidden: !isAlertCreated,
            }"
          >
            <div
              class="bottom-auto mt-2 flex rounded-sm bg-green-50 outline outline-1 outline-black/5"
            >
              <div
                class="flex items-center justify-center px-3 py-1 sm:px-5 sm:py-2"
              >
                <span class="text-green-500">
                  <CheckCircleIcon class="h-5 w-5" />
                </span>
                <div
                  aria-live="polite"
                  aria-atomic="true"
                  class="ml-2 inline-block"
                >
                  <RichText
                    class="text-sm text-green-800"
                    :blok="blok"
                    :rich-text-content="successMessage"
                    inline
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- Request error -->
          <div
            class="opacity-0"
            :class="{
              'opacity-95': showRequestError,
              hidden: !showRequestError,
            }"
          >
            <div
              class="bottom-auto mt-2 flex rounded-sm bg-yellow-50 outline outline-1 outline-black/5"
            >
              <div
                class="flex items-center justify-center px-3 py-1 sm:px-5 sm:py-2"
              >
                <span class="text-yellow-500">
                  <ExclamationCircleIcon class="h-6 w-6" />
                </span>
                <span
                  aria-live="polite"
                  aria-atomic="true"
                  class="ml-2 inline-block cursor-pointer text-sm text-yellow-800"
                >
                  Er is iets niet helemaal goed gegaan. Probeer het later nog
                  eens.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StandardWrapper>
  </LazyHydrate>
</template>

<script setup>
import { ref, reactive, computed, onBeforeUnmount, nextTick } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { renderRichText } from '@storyblok/vue'
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
} from '@heroicons/vue/20/solid'
import IconSpinner from '../icons/IconSpinner.vue'
import StandardWrapper from '../components/StandardWrapper.vue'
import RichText from '../components/RichText.vue'
import { createJobAlert } from '../api/tooContentDelivery.js'
import ctaEventBus from '../ctaEventBus.js'
import { useColorPalette } from '../composables/colorPalette'
import { useHead } from '@unhead/vue'
import { usePageContext } from '../renderer/usePageContext'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})

const container = ref(null)
const emailInput = ref(null)
const pingAlert = ref(false)
const isAlertCreated = ref(false)
const showRequestError = ref(false)
const isLoading = ref(false)
const bounce = ref(false)

const state = reactive({
  email: null,
})
const rules = {
  email: {
    required,
    email,
  },
}
const v$ = useVuelidate(rules, state)
const { textColorClass, colorCustomClass, colorName } = useColorPalette()

const hydrationStrategy = computed(() => {
  return { whenIdle: true }
})
const searchTerm = computed(() => {
  return props.blok.searchTerm || ''
})
const employmentTypes = computed(() => {
  return convertToArray(props.blok.employmentType)
})
const disciplines = computed(() => {
  return convertToArray(props.blok.discipline)
})
const education = computed(() => {
  return convertToArray(props.blok.education)
})
const inputClass = computed(() => {
  let classes = 'border-gray-300 rounded-sm'
  if (bounce.value) {
    classes += ' animate-bounce'
  }
  if (style.value === 'alternative') {
    classes += ' shadow-inner lg:rounded-l-md'
  } else {
    classes += ' shadow-xs'
  }
  if (isAlertCreated.value) {
    classes += ' focus:outline-green-500 focus:border-green-500'
  } else {
    const color = colorName(props.blok.color || 'default')
    classes += ` focus:border-transparent focus-${color}`
  }
  return classes
})
const btnClass = computed(() => {
  const radiusClass = props.blok.roundedCorners ? 'rounded-full' : 'rounded-md'
  const color = colorName(props.blok.color || 'primary', null, '')
  return `btn-style-primary btn-style-primary-color-${color} ${radiusClass}`
})
const successMessage = computed(() => {
  const renderedText = renderRichText(props.blok.successMessage || '')
  if (
    !props.blok.successMessage ||
    !renderedText ||
    renderedText === '<p></p>'
  ) {
    return ` Je volgt nu ${props.context.company.name}! Wanneer er nieuwe vacatures zijn ontvang je deze dagelijks per e-mail.`
  }

  return props.blok.successMessage
})
const requiredEmailMessage = computed(() => {
  return (
    props.blok.requiredEmailMessage ||
    'Het veld e-mailadres is niet ingevuld. Vul hier je e-mailadres in.'
  )
})
const invalidEmailMessage = computed(() => {
  return (
    props.blok.invalidEmailMessage ||
    'Dit is geen juist e-mailadres. Vul hier een geldig e-mailadres in, bijvoorbeeld naam@gmail.com'
  )
})
const style = computed(() => {
  return props.blok.style || 'default'
})

onBeforeUnmount(() => {
  ctaEventBus.$off('company-jobalert')
})

function onHydration() {
  ctaEventBus.$on('company-jobalert', handleAction)
  if (props.context.user?.email) {
    state.email = props.context.user.email
  }
}
function handleAction() {
  container.value?.$el?.scrollIntoView({ behavior: 'smooth' })
  bounce.value = true

  setTimeout(() => {
    bounce.value = false
    nextTick(() => {
      // focus on first input
      emailInput.value.focus()
    })
  }, 2500)
}
async function checkForm(event) {
  if (isLoading.value) {
    return
  }
  pingAlert.value = false
  let alertHandler = null

  alertHandler && clearTimeout(alertHandler)

  event && event.preventDefault()

  state.email = state.email?.toLowerCase()
  validate()

  if (!v$.value.$invalid && !isAlertCreated.value) {
    try {
      isLoading.value = true
      await createJobAlert({
        payload: {
          frequency: 'D',
          active: true,
          email: state.email,
          searchQuery: {
            company_id: props.context.company?.id || null,
            search_term: searchTerm.value,
            employment_type: employmentTypes.value,
            discipline: disciplines.value,
            education: education.value,
          },
          labels: { company: props.context.company?.name || null },
        },
        domainSlug: props.context.domainSlug,
        baseTooUrl: props.context.baseTooUrl,
      })
      isAlertCreated.value = true

      // Submit gtm event
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'jrUniversalJobAlertSubscription',
      })
    } catch (err) {
      showRequestError.value = true
      console.error(err)
    } finally {
      isLoading.value = false
    }
  } else {
    emailInput.value.focus()
    pingAlert.value = true
    alertHandler = setTimeout(() => {
      pingAlert.value = false
    }, 1000)
  }
}
function handleInput() {
  isAlertCreated.value = false
  showRequestError.value = false
  v$.value.email.$reset()
}
function validate() {
  v$.value.$touch()
}
function convertToArray(blokProperty) {
  if (blokProperty) {
    if (Array.isArray(blokProperty)) {
      return blokProperty.map((item) => item.key || item)
    }

    return blokProperty.replaceAll(' ', '').split(',')
  }
  return []
}

const nonce = usePageContext().nonce
useHead(
  () => {
    // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
    // because v-bind generates style="..." and that's not allowed with CSP enabled.
    const style = [
      {
        key: props.blok._uid,
        nonce,
        innerHTML: `
          #form${props.blok._uid} {
            --btn-color: var(--${colorCustomClass(props.blok.color || 'default', null, '')});
            --btn-text: var(--${colorCustomClass('', props.blok.color || 'default', '')});
          }
      `,
      },
    ]

    return {
      style,
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>

<style scoped>
input:focus {
  outline-offset: -2px !important;
}
</style>
